import React, { useEffect, useState } from 'react';
import { Search, Edit, AlertTriangle } from 'lucide-react';
import { adminAPI } from '../../api/admin';
import toast from 'react-hot-toast';

const InventoryAdminPage: React.FC = () => {
  const [inventory, setInventory] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editStock, setEditStock] = useState('');

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      setIsLoading(true);
      const res = await adminAPI.getInventory();
      if (res.success && res.data) setInventory(res.data);
    } catch {
      toast.error('Failed to fetch inventory');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStock = async (id: string) => {
    try {
      await adminAPI.updateStock(id, parseInt(editStock));
      toast.success('Stock updated');
      setEditingId(null);
      fetchInventory();
    } catch {
      toast.error('Failed to update stock');
    }
  };

  const filtered = inventory.filter(
    (i) =>
      i.product_name?.toLowerCase().includes(search.toLowerCase()) ||
      i.sku?.toLowerCase().includes(search.toLowerCase())
  );

  const lowStock = inventory.filter((i) => i.stock_quantity <= 5).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs tracking-widest uppercase text-nude mb-1">Management</p>
          <h1 className="font-serif text-3xl text-primary">Inventory</h1>
        </div>
        {lowStock > 0 && (
          <div className="flex items-center space-x-2 bg-red-50 border border-red-200 px-4 py-2">
            <AlertTriangle size={14} className="text-red-500" />
            <span className="text-xs text-red-500 tracking-widest uppercase">
              {lowStock} Low Stock
            </span>
          </div>
        )}
      </div>

      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-nude" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="SEARCH BY PRODUCT OR SKU"
          className="w-full bg-white border border-light pl-9 pr-4 py-3 text-xs tracking-widest outline-none focus:border-primary transition-colors"
        />
      </div>

      <div className="bg-white border border-light overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-light">
              {['Product', 'SKU', 'Size', 'Color', 'Stock', 'Status', 'Actions'].map((h) => (
                <th key={h} className="px-6 py-3 text-left text-xs tracking-widest uppercase text-nude">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center">
                  <div className="skeleton h-6 w-48 mx-auto rounded" />
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-xs text-nude tracking-widest uppercase">
                  No inventory found
                </td>
              </tr>
            ) : (
              filtered.map((item) => (
                <tr
                  key={item.id}
                  className={`border-b border-light hover:bg-light/50 transition-colors ${
                    item.stock_quantity <= 5 ? 'bg-red-50/30' : ''
                  }`}
                >
                  <td className="px-6 py-4">
                    <p className="text-xs font-medium tracking-wide">
                      {item.product_name}
                    </p>
                    <p className="text-xs text-nude">{item.product_slug}</p>
                  </td>
                  <td className="px-6 py-4 text-xs text-nude font-mono">
                    {item.sku || '—'}
                  </td>
                  <td className="px-6 py-4 text-xs">{item.size || '—'}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      {item.color && (
                        <div
                          className="w-4 h-4 rounded-full border border-light"
                          style={{ backgroundColor: item.color_hex || '#ccc' }}
                        />
                      )}
                      <span className="text-xs">{item.color || '—'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {editingId === item.id ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          value={editStock}
                          onChange={(e) => setEditStock(e.target.value)}
                          className="w-16 border border-primary px-2 py-1 text-xs outline-none"
                          autoFocus
                        />
                        <button
                          onClick={() => handleUpdateStock(item.id)}
                          className="text-xs text-green-600 hover:text-green-700 tracking-widest uppercase"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="text-xs text-nude hover:text-primary tracking-widest uppercase"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <span className={`text-sm font-medium ${
                        item.stock_quantity === 0
                          ? 'text-red-500'
                          : item.stock_quantity <= 5
                          ? 'text-amber-600'
                          : 'text-primary'
                      }`}>
                        {item.stock_quantity}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs tracking-widest uppercase px-2 py-1 rounded ${
                      item.stock_quantity === 0
                        ? 'bg-red-50 text-red-500'
                        : item.stock_quantity <= 5
                        ? 'bg-amber-50 text-amber-600'
                        : 'bg-green-50 text-green-600'
                    }`}>
                      {item.stock_quantity === 0
                        ? 'Out'
                        : item.stock_quantity <= 5
                        ? 'Low'
                        : 'In Stock'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => {
                        setEditingId(item.id);
                        setEditStock(String(item.stock_quantity));
                      }}
                      className="text-nude hover:text-primary transition-colors"
                    >
                      <Edit size={14} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InventoryAdminPage;